import {
  BookOpen,
  CalendarDays,
  Megaphone,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

import { NOTICE_TYPES, type NoticeType } from '@/features/teacher/types/notices';

/**
 * How a notice is drawn, by the type its author actually chose.
 *
 * The label and the colours come from the shared `NOTICE_TYPES` map — the same
 * one the teacher's board and the institute's board are drawn from — so an
 * "Event" is the same green wherever a child, a teacher or the office reads it
 * (CLAUDE.md §3). Only the icon is added here, because a list of cards needs
 * one and a badge does not.
 *
 * What this replaced: a 212-line table that matched *keywords in the title* to
 * pick a colour, an icon and a badge **label**. A notice a teacher typed as
 * General was badged "Important" because its title contained the word
 * "meeting", and each theme carried invented marketing lines — "TOGETHER FOR A
 * BRIGHTER TOMORROW" — printed over a stock illustration. The badge now says
 * what the notice is.
 */
const NOTICE_ICON = {
  GENERAL: Megaphone,
  EVENT: CalendarDays,
  REMINDER: CalendarDays,
  ACADEMIC: BookOpen,
  IMPORTANT: TriangleAlert,
} as const satisfies Record<NoticeType, LucideIcon>;

export interface NoticeTheme {
  Icon: LucideIcon;
  label: string;
  /** For the icon plate: a soft tint, a border and the glyph's own colour. */
  iconBg: string;
  iconText: string;
  iconBorder: string;
  /** The type badge, in one string. */
  badge: string;
}

export function getNoticeTheme(noticeType: NoticeType): NoticeTheme {
  const type = NOTICE_TYPES[noticeType] ?? NOTICE_TYPES.GENERAL;

  return {
    Icon: NOTICE_ICON[noticeType] ?? NOTICE_ICON.GENERAL,
    label: type.label,
    iconBg: type.bg,
    iconText: type.color,
    iconBorder: type.border,
    badge: `${type.bg} ${type.color} ${type.border}`,
  };
}
